import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FormModal } from "./FormModal";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import useConfirm from '../../../UI/organisms/ConfirmDialog/use-confirm';
import Dropzone, { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = {
  borderColor: '#2196f3',
};

const parseRows = (data: any[][], columnAccessors: string[]): Record<string, any>[] => {
  return data.filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''))
    .map(row => {
      const obj: Record<string, any> = {};
      columnAccessors.forEach((accessor, i) => {
        let val = row[i] ?? null;
        // Convert Date objects (from Excel date cells) to ISO strings
        if (val instanceof Date) {
          val = val.toISOString();
        }
        obj[accessor] = val;
      });
      return obj;
    });
};

const handleFileSelect = (acceptedFiles, setValue, setValueFieldName, columnAccessors: string[] = [], hiddenColumnDefaults: Record<string, any> = {}) => {
  const file = acceptedFiles?.[0];
  if (!file) return;

  setValue(setValueFieldName, []);

  const isExcel = /\.xlsx?$/i.test(file.name);

  const reader = new FileReader();
  reader.onload = (e) => {
    let rawRows: any[][];

    if (isExcel) {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, dateNF: 'yyyy-mm-dd' }) as any[][];
    } else {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      rawRows = lines.map(line => line.split(',').map(v => v.trim()));
    }

    if (!rawRows || rawRows.length === 0) return;

    // Detect and skip an optional header row.
    // A row is considered a header if every non-empty cell is a non-numeric string.
    const firstRow = rawRows[0];
    const hasHeader = firstRow.length > 0 && firstRow.every(cell => {
      if (cell === null || cell === undefined || cell === '') return true;
      const str = String(cell).trim();
      return str !== '' && isNaN(Number(str));
    });
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;

    const rows = parseRows(dataRows, columnAccessors).map(row => ({ ...row, ...hiddenColumnDefaults }));
    setValue(setValueFieldName, rows);
  };

  if (isExcel) {
    reader.readAsArrayBuffer(file);
  } else {
    reader.readAsText(file);
  }
};

export const UploadButton = ({ confirm, entity, modalContext, handleSubmit, onSubmit, writeAccess, isAdmin, editTableProps, setValue, watch, field, control, fieldErrors, validation, getValues, data, rowIdentity, isUnique, required, register, errors, formState }) => {
  const { modal, setValueFieldName, columnAccessors, hiddenColumnDefaults } = field;
  const { form, submitAction, repository, size, title } = modal || {};
  const [isAwaiting, setIsAwaiting] = useState(false);
  const [dynamicIcon, setDynamicIcon] = useState(null);
  /*   const [dynamicIconStyle, setDynamicIconStyle] = useState(null); */

  useEffect(() => {
    if (dynamicIcon) {
      const timer = setTimeout(() => {
        setDynamicIcon(null);
      }, 3000); // Hide the element after 5 seconds

      return () => clearTimeout(timer); // Clear the timer on unmount to prevent memory leaks
    }

  }, [dynamicIcon]);

  const redirect = useNavigate();
  const hide = field.hideIf ? field.hideIf({ editTableProps, formState, watch }) : false;

  const [localWrite] = usePermissions(field.permissions);

  const fileInputRef = useRef(null);

  const {
    isFocused,
  } = useDropzone();

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isFocused ? focusedStyle : {}),
  }), [
    isFocused,
  ]) as any;

  return (
    <>{!hide &&
      <Dropzone
        onDrop={(accepted) => { handleFileSelect(accepted, setValue, setValueFieldName, columnAccessors, hiddenColumnDefaults); }}
        accept={{
          'text/csv': ['.csv'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls'],
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <section >
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>Click or Drag & Drop a CSV or Excel file here...</p>
              {/*               <p>Click or Drag & Drop files here...</p> */}
            </div>
          </section>
        )}
      </Dropzone>
    }
    </>
  );
}